import CustomSnackBar from "../components/shared/CustomSnackBar";
import DuplicatesBanner from "../components/participants/DuplicatesBanner";
import DuplicatesManager from "../components/participants/DuplicatesManager";
import HistoricalDataBanner from "../components/participants/HistoricalDataBanner";
import ParticipantAdd from "../components/users/participants/ParticipantAdd";
import ParticipantsList from "../components/users/participants/ParticipantsList";
import TestDataGenerator from "../components/test-data/TestDataGenerator";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle
  } from "@mui/material";
import { Close } from "@mui/icons-material";
import { ParticipantWithId } from "../interfaces/IParticipants";
import { useHistoricalData } from "../hooks/useHistoricalData";
import { useParticipantsContext } from "../contexts/ParticipantsContext";
import { useState } from "react";
import { useUserContext } from "../contexts/UserContext";

export default function ParticipantsFeature() {
  const {
    participants,
    isLoading,
    handleParticipantUpdate,
    handleParticipantDelete,
    refreshParticipants,
  } = useParticipantsContext();
  const { user, refreshUser } = useUserContext();
  const [editingParticipantId, setEditingParticipantId] = useState<string | null>(null);
  const [showDuplicates, setShowDuplicates] = useState<boolean>(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Hook do zarządzania danymi historycznymi
  const {
    archiveData,
    status,
    canImport,
    loading: historicalDataLoading,
    error: historicalDataError,
    handleImport,
    handleReject,
    handleSkip,
  } = useHistoricalData(user, refreshParticipants, refreshUser);

  const handleEditParticipant = (participantId: string) => {
    setEditingParticipantId(participantId);
    setEditError(null); // Wyczyść błędy przy rozpoczęciu edycji
  };

  const handleSaveEdit = async (participantId: string, updatedData: Partial<ParticipantWithId>) => {
    // Wyczyść poprzednie błędy
    setEditError(null);
    // Delegacja do hooka - tylko orkiestracja
    const result = await handleParticipantUpdate(participantId, updatedData);

    if (result.success) {
      setEditingParticipantId(null);
      setEditError(null);
    } else {
      setEditError(result.error || "Wystąpił błąd podczas aktualizacji danych zawodnika.");
    }
  };

  const handleCancelEdit = () => {
    setEditingParticipantId(null);
    setEditError(null); // Wyczyść błędy przy anulowaniu
  };

  const handleRemoveParticipant = async (participantId: string) => {
    // Delegacja do hooka - tylko orkiestracja
    const result = await handleParticipantDelete(participantId);

    if (!result.success) {
      console.error("Błąd podczas usuwania zawodnika:", result.error);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2 }}>
      {/* TEST DATA GENERATOR - WYKOMENTOWAĆ PRZED WYSTAWIANIEM NA SERWERZE */}
      {/* <TestDataGenerator /> */}

      {/* Banner z danymi historycznymi */}
      <HistoricalDataBanner
        archiveData={archiveData}
        status={status}
        canImport={canImport}
        loading={historicalDataLoading}
        error={historicalDataError}
        onImport={handleImport}
        onReject={handleReject}
        onSkip={handleSkip}
      />

      {/* Banner duplikatów */}
      <DuplicatesBanner onOpen={() => setShowDuplicates(true)} />

      {/* Formularz dodawania zawodnika */}
      <ParticipantAdd disabled={editingParticipantId !== null} />

      {/* Lista zawodników */}
      <ParticipantsList
        participants={participants}
        isLoading={isLoading}
        title={`Zawodnicy (${participants.length})`}
        emptyMessage="Brak zawodników"
        onEditParticipant={handleEditParticipant}
        onRemoveParticipant={handleRemoveParticipant}
        isEditingMode={editingParticipantId !== null}
        editingParticipantId={editingParticipantId}
        onSaveEdit={handleSaveEdit}
        onCancelEdit={handleCancelEdit}
      />

      {/* Snackbar z błędami walidacji */}
      <CustomSnackBar
        open={!!editError}
        onClose={() => setEditError(null)}
        type="error"
        title="Błąd walidacji"
        message={editError || ""}
        autoHideDuration={6000}
      />

      {/* Manager duplikatów */}
      <Dialog
        open={showDuplicates}
        onClose={() => setShowDuplicates(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "flex-end", py: 1 }}>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<Close fontSize="small" />}
            onClick={() => setShowDuplicates(false)}
            sx={{
              py: 0.75,
              px: 1.25,
              minHeight: 0,
              lineHeight: 1.2,
              fontSize: "0.75rem",
              textTransform: "none",
            }}
          >
            Zamknij
          </Button>
        </DialogTitle>
        <DialogContent>
          <DuplicatesManager onClose={() => setShowDuplicates(false)} />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
